import { FC, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { createBemBlockBuilder, emitScrollTo } from '@ebsco-ui/ebsco-ui';

import { SearchResults } from '@app/components/Search/SearchResultsPage/SearchResults';
import { AddToProjectModalProvider } from '@app/components';
import { useSearchContext } from '@app/contexts';
import { usePaginationItemFocus } from '@app/hooks';
import { setFocusOn } from '@app/utils';

import { FilterPanel } from './FilterPanel';

import './SearchResultsPage.scss';

const cnBem = createBemBlockBuilder(['searchResultsPage']);

export const SearchResultsPage: FC = () => {
  const { $t } = useIntl();
  const {
    state: { query, pageNumber },
    shouldRefocusRef,
    searchResults: {
      searchResultsQuery: { isFetching, status },
      pages,
      currentPage,
    },
    instanceIdScrollTo,
    isPageFromBackButton,
    setInstanceIdScrollTo,
    setIsPageFromBackButton,
  } = useSearchContext();

  const mainContainerRef = useRef<HTMLDivElement>(null!);
  const { search: urlQuery } = useLocation();

  usePaginationItemFocus({
    pageNumber,
    totalPagesNumber: pages.length,
    instanceIdScrollTo,
  });

  useEffect(() => {
    if ((!isFetching || !isEmpty(currentPage)) && shouldRefocusRef.current) {
      mainContainerRef.current.focus({ preventScroll: true });
      emitScrollTo(0, undefined);
      shouldRefocusRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    if (
      !isFetching &&
      urlQuery &&
      (status === 'success' || status === 'error')
    ) {
      mainContainerRef.current.focus({ preventScroll: true });
      emitScrollTo(0, undefined);
    }

    if (isFetching) {
      shouldRefocusRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (instanceIdScrollTo && !isEmpty(pages)) {
      const selector = `[data-link="${instanceIdScrollTo}-link"]`;

      setFocusOn({ selector });
      setInstanceIdScrollTo(null);
    } else if (!instanceIdScrollTo && isPageFromBackButton) {
      const element = document.querySelector('[data-link]') as HTMLElement;

      setFocusOn({ element });
      setIsPageFromBackButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  return (
    <>
      <Helmet>
        <title>
          {$t(
            {
              id: 'searchResults.documentTitle',
              defaultMessage:
                '{searchTerm, select, null {EBSCO Locate} other {{searchTerm} - Search results - EBSCO Locate}}',
            },
            { searchTerm: query || null }
          )}
        </title>
      </Helmet>
      <div
        className={cn('container', cnBem('__container'))}
        data-auto="search-page-wrapper"
      >
        <FilterPanel />
        <main
          className={cnBem('__wrapper')}
          data-auto="search-results-wrapper"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          ref={mainContainerRef}
          data-focus="focusedNode"
        >
          <AddToProjectModalProvider>
            <SearchResults />
          </AddToProjectModalProvider>
        </main>
      </div>
    </>
  );
};
